import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createQuestion,
  listQuestions,
  replyToQuestion,
  resolveQuestion,
  unresolveQuestion,
} from "../../api/boardbacks.api";
import { hasLegacyAdminFallback } from "../../accessControl";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import Textarea from "../../ui/Textarea";

const getActorLabel = (actor, fallbackId = null) => {
  if (actor?.username) return `@${actor.username}`;
  if (actor?.email) return actor.email;
  if (fallbackId) return `User #${fallbackId}`;
  return "Unknown user";
};

const getQuestionContextLabel = (question, currentEntry) => {
  if (question?.agenda_attachment_id && currentEntry?.doc?.agenda_attachment_id) {
    return String(question.agenda_attachment_id) === String(currentEntry.doc.agenda_attachment_id)
      ? "Current agenda document"
      : "Another agenda document";
  }
  if (question?.document_id && currentEntry?.doc?.id) {
    return String(question.document_id) === String(currentEntry.doc.id)
      ? "Current pack document"
      : "Another pack document";
  }
  if (question?.agenda_item_id && currentEntry?.agendaItemId) {
    return String(question.agenda_item_id) === String(currentEntry.agendaItemId)
      ? "Current agenda item"
      : "Another agenda item";
  }
  return "Pack question";
};

const sameQuestionList = (left, right) => {
  const a = Array.isArray(left) ? left : [];
  const b = Array.isArray(right) ? right : [];
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((question, index) => {
    const candidate = b[index];
    return (
      String(question?.id || "") === String(candidate?.id || "") &&
      String(question?.status || "") === String(candidate?.status || "") &&
      String(question?.body || "") === String(candidate?.body || "") &&
      String(question?.resolved_at || "") === String(candidate?.resolved_at || "") &&
      String(question?.page_number || "") === String(candidate?.page_number || "") &&
      (Array.isArray(question?.messages) ? question.messages.length : 0) ===
        (Array.isArray(candidate?.messages) ? candidate.messages.length : 0)
    );
  });
};

const mergeQuestionLists = (current, incoming) => {
  const currentList = Array.isArray(current) ? current : [];
  const incomingList = Array.isArray(incoming) ? incoming : [];
  if (incomingList.length === 0) {
    return currentList;
  }

  const merged = [...incomingList];
  const incomingIds = new Set(incomingList.map((question) => String(question?.id || "")));
  currentList.forEach((question) => {
    if (!incomingIds.has(String(question?.id || ""))) {
      merged.push(question);
    }
  });
  return merged;
};

const joinQuestionMeta = (parts) => parts.filter(Boolean).join(" · ");

export default function BoardPackQuestionsPanel({
  pack,
  currentEntry = null,
  currentUser = null,
  canParticipate = true,
  open = false,
  questions: externalQuestions,
  questionsLoading = false,
  questionsError = null,
  onQuestionsChange,
  onRefetchQuestions,
}) {
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [draftVisibility, setDraftVisibility] = useState("private");
  const [draftMentionedUserId, setDraftMentionedUserId] = useState("");
  const [replyOpenId, setReplyOpenId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [actionError, setActionError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [replyingId, setReplyingId] = useState(null);
  const [displayQuestions, setDisplayQuestions] = useState([]);
  const [questionToRevealId, setQuestionToRevealId] = useState(null);
  const questionRefs = useRef(new Map());
  const composerInitialised = useRef(false);
  const [showComposer, setShowComposer] = useState(false);

  const packId = pack?.id;
  const currentDoc = currentEntry?.doc || null;
  const currentContextLabel =
    currentEntry?.agendaTitle || currentDoc?.original_filename || currentDoc?.filename || "Current document";
  const currentAttachmentId = currentDoc?.agenda_attachment_id || currentDoc?.id || null;
  const currentDocumentId =
    currentDoc?.document_id || (!currentDoc?.agenda_attachment_id ? currentDoc?.id : null) || null;
  const isAdmin = hasLegacyAdminFallback(currentUser);
  const hasExternalQuestions = externalQuestions !== undefined;

  const {
    data: queriedQuestions = [],
    refetch,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["boardback-questions", packId],
    queryFn: () => listQuestions(packId),
    enabled: open && Boolean(packId) && !hasExternalQuestions,
  });

  const questionSource = hasExternalQuestions ? externalQuestions : queriedQuestions;
  const loadingQuestions = hasExternalQuestions ? questionsLoading : isLoading;
  const questionLoadFailed = hasExternalQuestions ? Boolean(questionsError) : isError;
  const questionLoadError = hasExternalQuestions ? questionsError : error;
  const refreshQuestions = onRefetchQuestions || refetch;

  useEffect(() => {
    const incomingQuestions = Array.isArray(questionSource) ? questionSource : [];
    setDisplayQuestions((prev) => {
      const mergedQuestions = mergeQuestionLists(prev, incomingQuestions);
      return sameQuestionList(prev, mergedQuestions) ? prev : mergedQuestions;
    });
  }, [questionSource]);

  useEffect(() => {
    if (composerInitialised.current || loadingQuestions) return;
    setShowComposer(!Array.isArray(questionSource) || questionSource.length === 0);
    composerInitialised.current = true;
  }, [loadingQuestions, questionSource]);

  useEffect(() => {
    if (!questionToRevealId) return;
    const questionNode = questionRefs.current.get(String(questionToRevealId));
    if (!questionNode) return;
    questionNode.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    questionNode.focus({ preventScroll: true });
    setQuestionToRevealId(null);
  }, [displayQuestions, questionToRevealId]);

  const attendees = useMemo(() => {
    const raw = Array.isArray(pack?.attendees) ? pack.attendees : [];
    return raw
      .filter((attendee) => attendee?.id)
      .sort((a, b) => String(a.username || a.email || "").localeCompare(String(b.username || b.email || "")));
  }, [pack?.attendees]);

  const sortedQuestions = useMemo(() => {
    const questionList = Array.isArray(displayQuestions) ? [...displayQuestions] : [];
    return questionList.sort((a, b) => {
      const aCurrent =
        (a.agenda_attachment_id && currentAttachmentId && String(a.agenda_attachment_id) === String(currentAttachmentId)) ||
        (a.document_id && currentDocumentId && String(a.document_id) === String(currentDocumentId)) ||
        (a.agenda_item_id && currentEntry?.agendaItemId && String(a.agenda_item_id) === String(currentEntry.agendaItemId));
      const bCurrent =
        (b.agenda_attachment_id && currentAttachmentId && String(b.agenda_attachment_id) === String(currentAttachmentId)) ||
        (b.document_id && currentDocumentId && String(b.document_id) === String(currentDocumentId)) ||
        (b.agenda_item_id && currentEntry?.agendaItemId && String(b.agenda_item_id) === String(currentEntry.agendaItemId));
      if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
      if ((a.status === "resolved") !== (b.status === "resolved")) {
        return a.status === "resolved" ? 1 : -1;
      }
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    });
  }, [displayQuestions, currentAttachmentId, currentDocumentId, currentEntry?.agendaItemId]);

  useEffect(() => {
    if (!open) {
      setReplyOpenId(null);
      setActionError("");
    }
  }, [open]);

  const resetComposer = () => {
    setDraftTitle("");
    setDraftBody("");
    setDraftVisibility("private");
    setDraftMentionedUserId("");
  };

  const updateQuestions = (nextQuestion) => {
    if (!nextQuestion?.id) return;
    const current = Array.isArray(displayQuestions) ? displayQuestions : [];
    const existingIndex = current.findIndex((question) => String(question?.id) === String(nextQuestion.id));
    if (existingIndex < 0) {
      const nextQuestions = [nextQuestion, ...current];
      setDisplayQuestions(nextQuestions);
      onQuestionsChange?.(nextQuestions);
      return;
    }
    const nextQuestions = [...current];
    nextQuestions[existingIndex] = nextQuestion;
    setDisplayQuestions(nextQuestions);
    onQuestionsChange?.(nextQuestions);
  };

  const upsertQuestion = (nextQuestion) => {
    if (!nextQuestion?.id) return;
    updateQuestions(nextQuestion);
  };

  const handleCreateQuestion = async () => {
    if (!packId || !canParticipate) return;
    const trimmedBody = draftBody.trim();
    if (!trimmedBody) {
      setActionError("Add your question before sending it.");
      return;
    }
    setSubmitting(true);
    setActionError("");
    try {
      const mentionedUser = attendees.find((attendee) => String(attendee.id) === String(draftMentionedUserId));
      const mentionPrefix =
        mentionedUser && !trimmedBody.includes(`@${mentionedUser.username}`)
          ? `@${mentionedUser.username} `
          : "";
      const createdQuestion = await createQuestion(packId, {
        title: draftTitle.trim() || `Question on ${currentContextLabel}`,
        body: `${mentionPrefix}${trimmedBody}`.trim(),
        agenda_item_id: currentEntry?.agendaItemId || null,
        document_id: currentDocumentId || null,
        agenda_attachment_id: currentAttachmentId || null,
        page_number: currentEntry?.currentPage || null,
        mentioned_user_id: draftMentionedUserId ? Number(draftMentionedUserId) : null,
        visibility: draftVisibility,
      });
      upsertQuestion(createdQuestion);
      resetComposer();
      setShowComposer(false);
      setQuestionToRevealId(createdQuestion.id);
      await Promise.resolve(refreshQuestions?.()).catch(() => undefined);
    } catch (nextError) {
      setActionError(nextError?.response?.data?.detail || nextError?.message || "Failed to send the question.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (questionId) => {
    if (!questionId || !canParticipate) return;
    const body = String(replyDrafts[questionId] || "").trim();
    if (!body) {
      setActionError("Add a reply before sending it.");
      return;
    }
    setReplyingId(questionId);
    setActionError("");
    try {
      const updatedQuestion = await replyToQuestion(questionId, { body });
      upsertQuestion(updatedQuestion);
      setReplyDrafts((prev) => ({ ...prev, [questionId]: "" }));
      setReplyOpenId(null);
      await Promise.resolve(refreshQuestions?.()).catch(() => undefined);
    } catch (nextError) {
      setActionError(nextError?.response?.data?.detail || nextError?.message || "Failed to send the reply.");
    } finally {
      setReplyingId(null);
    }
  };

  const handleResolveToggle = async (question) => {
    const questionId = question?.id;
    if (!questionId || !canParticipate) return;
    setResolvingId(questionId);
    setActionError("");
    try {
      const updatedQuestion =
        question?.status === "resolved"
          ? await unresolveQuestion(questionId)
          : await resolveQuestion(questionId);
      upsertQuestion(updatedQuestion);
      await Promise.resolve(refreshQuestions?.()).catch(() => undefined);
    } catch (nextError) {
      setActionError(
        nextError?.response?.data?.detail ||
          nextError?.message ||
          (question?.status === "resolved" ? "Failed to reopen the question." : "Failed to resolve the question.")
      );
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <aside
      aria-label="Questions panel"
      style={{
        width: 360,
        minWidth: 360,
        height: "100%",
        borderLeft: "1px solid #dbe4ee",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "16px 18px",
          borderBottom: "1px solid #e5e7eb",
          display: "grid",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <div style={{ fontWeight: 700, color: "#0f172a" }}>Questions</div>
          {!showComposer ? (
            <Button variant="secondary" onClick={() => setShowComposer(true)} disabled={!canParticipate}>
              New question
            </Button>
          ) : null}
        </div>
        <div style={{ fontSize: 12, color: "#64748b" }}>
          {isAdmin
            ? "Admins can review every question, including private attendee threads."
            : "Private questions are visible only to the author, the mentioned attendee, and admins."}
        </div>
        <div
          style={{
            padding: 10,
            borderRadius: 10,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            display: "grid",
            gap: 4,
          }}
        >
          <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Current context
          </div>
          <div style={{ fontWeight: 600, color: "#0f172a" }}>
            {currentDoc?.original_filename || currentDoc?.filename || currentContextLabel}
          </div>
          {currentEntry?.agendaTitle ? <div style={{ fontSize: 12, color: "#475569" }}>{currentEntry.agendaTitle}</div> : null}
          {currentEntry?.currentPage ? <div style={{ fontSize: 12, color: "#0f766e" }}>Linked page: {currentEntry.currentPage}</div> : null}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16, display: "grid", gap: 12 }}>
        {loadingQuestions ? <div style={{ color: "#64748b", fontSize: 13 }}>Loading questions...</div> : null}
        {!loadingQuestions && questionLoadFailed ? (
          <div style={{ color: "#b91c1c", fontSize: 13 }}>
            {questionLoadError?.detail || questionLoadError?.message || "Failed to load questions."}
          </div>
        ) : null}
        {!loadingQuestions && sortedQuestions.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: 13 }}>
            No questions yet for this pack. Use the composer below to ask the first one.
          </div>
        ) : null}

        {sortedQuestions.map((question) => {
          const authorLabel = getActorLabel(question.author, question.author_id);
          const mentionedLabel = question.mentioned_user
            ? getActorLabel(question.mentioned_user, question.mentioned_user_id)
            : "";
          const isReplyOpen = replyOpenId === question.id;
          const isResolved = question.status === "resolved";
          const canResolveQuestion =
            canParticipate && (isAdmin || String(currentUser?.id || "") === String(question.author_id || ""));
          return (
            <div
              key={question.id}
              ref={(node) => {
                if (node) questionRefs.current.set(String(question.id), node);
                else questionRefs.current.delete(String(question.id));
              }}
              tabIndex={-1}
              data-question-id={question.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 12,
                background: "#ffffff",
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                <div style={{ display: "grid", gap: 4 }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{question.title}</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {joinQuestionMeta([
                      authorLabel,
                      mentionedLabel ? `to ${mentionedLabel}` : null,
                      getQuestionContextLabel(question, currentEntry),
                      question.page_number ? `Page ${question.page_number}` : null,
                    ])}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "4px 8px",
                      borderRadius: 999,
                      background: question.visibility === "shared" ? "#dcfce7" : "#fef3c7",
                      color: question.visibility === "shared" ? "#166534" : "#92400e",
                    }}
                  >
                    {question.visibility === "shared" ? "All attendees" : "Private"}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "4px 8px",
                      borderRadius: 999,
                      background: question.status === "resolved" ? "#e0f2fe" : "#ede9fe",
                      color: question.status === "resolved" ? "#075985" : "#5b21b6",
                    }}
                  >
                    {question.status === "resolved" ? "Resolved" : "Open"}
                  </span>
                </div>
              </div>

              <div style={{ color: "#334155", fontSize: 13, lineHeight: 1.5 }}>{question.body}</div>

              {Array.isArray(question.messages) && question.messages.length > 0 ? (
                <div style={{ display: "grid", gap: 8, paddingTop: 6, borderTop: "1px solid #f1f5f9" }}>
                  {question.messages.map((message) => (
                    <div
                      key={message.id}
                      style={{
                        background: "#f8fafc",
                        borderRadius: 10,
                        padding: "8px 10px",
                        display: "grid",
                        gap: 4,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>
                        {getActorLabel(message.author, message.author_id)}
                      </div>
                      <div style={{ fontSize: 13, color: "#334155" }}>{message.body}</div>
                    </div>
                  ))}
                </div>
              ) : null}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  variant="secondary"
                  onClick={() => setReplyOpenId((prev) => (prev === question.id ? null : question.id))}
                  disabled={!canParticipate}
                >
                  {isReplyOpen ? "Cancel reply" : isResolved ? "Continue conversation" : "Reply"}
                </Button>
                {canResolveQuestion ? (
                  <Button
                    variant="secondary"
                    onClick={() => handleResolveToggle(question)}
                    disabled={resolvingId === question.id}
                  >
                    {resolvingId === question.id
                      ? isResolved
                        ? "Reopening..."
                        : "Resolving..."
                      : isResolved
                        ? "Unresolve"
                        : "Resolve"}
                  </Button>
                ) : null}
              </div>

              {isReplyOpen ? (
                <div style={{ display: "grid", gap: 8 }}>
                  <Textarea
                    value={replyDrafts[question.id] || ""}
                    onChange={(e) => setReplyDrafts((prev) => ({ ...prev, [question.id]: e.target.value }))}
                    placeholder="Write a reply"
                    style={{ minHeight: 88 }}
                  />
                  <Button
                    variant="primary"
                    onClick={() => handleReply(question.id)}
                    disabled={!canParticipate || replyingId === question.id}
                  >
                    {replyingId === question.id ? "Sending..." : "Send reply"}
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {showComposer ? (
      <div style={{ borderTop: "1px solid #e5e7eb", display: "flex", flexDirection: "column", flex: "0 0 auto", minHeight: 0 }}>
        <div
          style={{
            padding: "12px 16px 10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            background: "#ffffff",
            position: "sticky",
            top: 0,
            zIndex: 1,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>Ask question</div>
          <Button variant="secondary" onClick={() => setShowComposer(false)} disabled={submitting}>
            Cancel
          </Button>
        </div>
        <div style={{ padding: "16px 16px 10px", display: "grid", gap: 10, maxHeight: 310, overflowY: "auto" }}>
          <Input
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="Question title"
          />
          <Select
            value={draftMentionedUserId}
            onChange={(e) => setDraftMentionedUserId(e.target.value)}
            options={[
              { value: "", label: "No @mention" },
              ...attendees.map((attendee) => ({
                value: String(attendee.id),
                label: `@${attendee.username}${attendee.email ? ` (${attendee.email})` : ""}`,
              })),
            ]}
          />
          <Select
            value={draftVisibility}
            onChange={(e) => setDraftVisibility(e.target.value)}
            options={[
              { value: "private", label: "Private question" },
              { value: "shared", label: "Visible to all attendees" },
            ]}
          />
          <Textarea
            value={draftBody}
            onChange={(e) => setDraftBody(e.target.value)}
            placeholder="Write your question here"
            style={{ minHeight: 110 }}
          />
          <div style={{ fontSize: 12, color: "#64748b" }}>
            Mentioning an attendee adds an `@username` tag and keeps the thread directed at that meeting member.
          </div>
          {actionError ? <div style={{ fontSize: 12, color: "#b91c1c" }}>{actionError}</div> : null}
        </div>
        <div style={{ padding: "10px 16px 16px", background: "#ffffff", borderTop: "1px solid #f1f5f9", position: "sticky", bottom: 0, zIndex: 1, flexShrink: 0 }}>
          <Button
            onClick={handleCreateQuestion}
            disabled={!canParticipate || submitting || !draftBody.trim()}
            variant="primary"
            style={{ width: "100%" }}
          >
            {submitting ? "Sending..." : "Ask question"}
          </Button>
        </div>
      </div>
      ) : null}

    </aside>
  );
}
